import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getLangFromPath } from "@/lib/i18nRoutes";

const DISMISS_KEY = "marstick_pwa_install_dismissed_v1";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function detectIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function detectSafari() {
  const ua = window.navigator.userAgent || "";
  return /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Android/i.test(ua);
}

function safeGetDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === "true";
  } catch {
    return false;
  }
}

function safeSetDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, "true");
  } catch {
    // Storage may be unavailable in private browsing; ignore safely.
  }
}

export default function PWAInstallBanner() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [visible, setVisible] = React.useState(false);
  const [installed, setInstalled] = React.useState(false);
  const [updateReady, setUpdateReady] = React.useState(false);
  const lang = getLangFromPath(location.pathname) || "en";

  React.useEffect(() => {
    const onUpdateReady = () => setUpdateReady(true);
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    window.addEventListener("marstick:pwa-update-ready", onUpdateReady);
    navigator.serviceWorker?.addEventListener("controllerchange", onControllerChange);
    return () => {
      window.removeEventListener("marstick:pwa-update-ready", onUpdateReady);
      navigator.serviceWorker?.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined" || isStandalone() || safeGetDismissed()) return;

    const iosSafari = detectIOS() && detectSafari();
    const showTimer = window.setTimeout(() => {
      if (iosSafari) setVisible(true);
    }, 7000);

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      window.setTimeout(() => setVisible(true), 5000);
    };

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      safeSetDismissed();
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.clearTimeout(showTimer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const refreshApp = () => {
    navigator.serviceWorker?.getRegistration?.().then((registration) => {
      registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    });
    window.setTimeout(() => window.location.reload(), 800);
  };

  if (updateReady) {
    return (
      <aside className="fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] z-[95] mx-auto max-w-md rounded-[1.4rem] border border-white/70 bg-slate-950 p-3 text-white shadow-2xl shadow-ocean/25 ring-1 ring-white/20 backdrop-blur-2xl md:bottom-5" role="status" aria-live="polite">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Download className="h-5 w-5" aria-hidden="true" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black">New version available</p>
            <p className="text-xs leading-5 text-cyan-50/80">Refresh when ready to update Marstick safely.</p>
          </div>
          <Button size="sm" onClick={refreshApp} className="rounded-full bg-white text-ocean hover:bg-cyan-50">Refresh</Button>
        </div>
      </aside>
    );
  }

  if (!visible || installed) return null;

  const iosSafari = detectIOS() && detectSafari();
  const guidePath = `/${lang}/install`;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  return (
    <aside className="fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] z-[95] mx-auto max-w-md rounded-[1.4rem] border border-white/70 bg-white/95 p-3 text-slate-950 shadow-2xl shadow-ocean/25 ring-1 ring-white/60 backdrop-blur-2xl md:bottom-5" role="dialog" aria-label="Install Marstick app">
      <button onClick={dismiss} className="absolute right-2 top-2 rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Dismiss install suggestion">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="flex gap-3 pr-8">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
          {iosSafari ? <Share className="h-5 w-5" aria-hidden="true" /> : <Download className="h-5 w-5" aria-hidden="true" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black">Install Marstick</p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {iosSafari ? "On iPhone Safari: tap Share → Add to Home Screen → Add." : "Add Marstick to your home screen for an app-like maritime dashboard."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {deferredPrompt && <Button size="sm" onClick={install} className="rounded-full">Install Marstick</Button>}
            <Button asChild size="sm" variant="outline" className="rounded-full bg-white">
              <Link to={guidePath}>Install guide</Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="rounded-full">Maybe later</Button>
          </div>
        </div>
      </div>
    </aside>
  );
}